import React, { useState, useRef, useEffect } from "react";
import { ContentType } from "./LibraryLayout";

interface FilterDropdownProps {
  onlyNonLeadCarousel: boolean;
  onNonLeadCarouselChange: (checked: boolean) => void;
  onlyPublic: boolean;
  onOnlyPublicChange: (checked: boolean) => void;
  contentType?: ContentType;
  availableTags?: string[];
  selectedTags?: string[];
  onSelectedTagsChange?: (tags: string[]) => void;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  onlyNonLeadCarousel,
  onNonLeadCarouselChange,
  onlyPublic,
  onOnlyPublicChange,
  contentType,
  availableTags = [],
  selectedTags = [],
  onSelectedTagsChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const showCarouselFilter = contentType === "images";

  const hasTagsSelected = selectedTags.length > 0;
  const activeFilterCount =
    (showCarouselFilter && onlyNonLeadCarousel ? 1 : 0) +
    (onlyPublic ? 1 : 0) +
    (hasTagsSelected ? 1 : 0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  /**
   * Toggle a website tag in/out of the selected set.
   */
  const handleTagToggle = (tag: string) => {
    if (!onSelectedTagsChange) return;
    const next = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    onSelectedTagsChange(next);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
          activeFilterCount > 0
            ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
        Filters
        {activeFilterCount > 0 && (
          <span className="bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded-full min-w-5 text-center">
            {activeFilterCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-h-96 overflow-y-auto">
          {showCarouselFilter && (
            <>
              <label className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={onlyNonLeadCarousel}
                  onChange={(e) => onNonLeadCarouselChange(e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded-sm focus:ring-purple-500 focus:ring-2 cursor-pointer"
                />
                <span className="text-sm text-gray-700">Non-lead carousel items only</span>
              </label>

              <div className="mx-4 my-1 h-px bg-gray-200" />
            </>
          )}

          <label className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={onlyPublic}
              onChange={(e) => onOnlyPublicChange(e.target.checked)}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded-sm focus:ring-purple-500 focus:ring-2 cursor-pointer"
            />
            <span className="text-sm text-gray-700">Public items only</span>
          </label>

          {availableTags.length > 0 && onSelectedTagsChange && (
            <>
              <div className="mx-4 my-2 h-px bg-gray-200" />
              <div className="px-4 py-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Website Tags
                </span>
              </div>
              {availableTags.map(tag => (
                <label
                  key={tag}
                  className="flex items-center gap-3 px-4 py-1.5 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag)}
                    onChange={() => handleTagToggle(tag)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded-sm focus:ring-purple-500 focus:ring-2 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">{tag}</span>
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
